/**
 * HeroSection — FULL PAGE immersif (redesign).
 *
 * Architecture en 3 couches empilées (z-index) dans un <section> 100svh :
 *
 *   z-0  (fond)    : <CycleCanvas panX={-1.6} /> — plein écran, draggable
 *   z-5  (scrim)   : dégradé horizontal + dégradé bas, pointer-events:none
 *   z-10 (contenu) : texte à gauche en overlay, pointer-events:none globalement
 *                    mais pointer-events:auto sur les éléments interactifs
 *
 * Câblage préservé :
 *   - `openSignup` → CTA orange appelle openSignup('parent')
 *   - `active` (-1..2) ↔ sélecteur de rôle ↔ CycleCanvas
 *   - drag canvas → onSelect(-1), clic nœud → onSelect(i)
 *
 * Responsive :
 *   - Desktop (≥900px) : anneau décalé à droite (panX=-1.6), scrim horizontal
 *   - Mobile (<900px)  : panX réduit (~-0.4), scrim renforcé, texte pleine largeur
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import logomark from '../../assets/logomark.png';

// Chargé en différé : sort three.js (~130ko) du bundle initial de la landing
// pour ne pas retarder le LCP (le titre). Le canvas est décoratif ; fallback vide.
const CycleCanvas = lazy(() => import('./CycleCanvas'));

// ─── Contenu des rôles ───────────────────────────────────────────────────────
const ROLES = [
  {
    label: 'Parent',
    title: "Vous ne relancez plus personne.",
    desc:  "Le planning, l'état de chaque séance et le compte-rendu du dernier cours arrivent à vous. Pas de messages perdus, pas de « ça s'est bien passé ? ».",
  },
  {
    label: 'Répétiteur',
    title: 'Votre pédagogie, pas votre paperasse.',
    desc:  'Créneaux générés automatiquement, notes IE et DS avec leur coefficient, moyennes calculées. Vous rédigez, MyEduCycle diffuse.',
  },
  {
    label: 'Élève',
    title: 'La progression devient visible.',
    desc:  "Notions vues, objectifs de la semaine, évolution par matière et par trimestre. L'élève voit son propre élan, pas seulement une note isolée.",
  },
];

const DEFAULT_PANEL = {
  title: 'Un cycle, trois points de vue.',
  desc:  'Sélectionnez un rôle — ou faites tourner le cycle — pour voir ce que chacun gagne à partager le même suivi.',
};

// ─── Composant ───────────────────────────────────────────────────────────────
export default function HeroSection({ openSignup, openLogin }) {
  // Rôle mis en avant (0 = Parent par défaut). Le cycle tourne en autonomie
  // en permanence ; `active` ne pilote que la mise en avant du nœud + le panneau.
  const [active, setActive] = useState(0);

  // Responsive : sous 900px l'anneau se recentre (panX réduit) et le scrim se renforce.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const panel = active >= 0 ? ROLES[active] : DEFAULT_PANEL;

  return (
    <section
      id="hero"
      className="relative overflow-hidden"
      style={{
        height:     '100svh',
        minHeight:  '100svh',
        background: '#0F2547',
        // Halo radial déplacé vers ~62% pour accompagner l'anneau décalé à droite.
        // Sur mobile il revient vers le centre (géré via le scrim renforcé).
        backgroundImage: 'radial-gradient(1200px 800px at 62% 48%, rgba(110,140,184,.28), rgba(15,37,71,0) 60%)',
      }}
    >

      {/* ══ COUCHE FOND z-0 : canvas plein écran ════════════════════════════
          Le canvas est le fond. Il est draggable ; rien au-dessus ne doit
          bloquer les pointer events dans la zone droite (vide de texte).    */}
      <div
        className="absolute inset-0"
        style={{ zIndex: 0 }}
        aria-hidden="true"
      >
        <Suspense fallback={null}>
          <CycleCanvas
            active={active}
            onSelect={setActive}
            accent="#4CE0C2"
            panX={isMobile ? -0.2 : -1.6}
          />
        </Suspense>
      </div>

      {/* ══ COUCHE SCRIM z-5 : dégradés de lisibilité ════════════════════
          pointer-events:none — laisse tous les clics/drags traverser vers
          le canvas en dessous.

          Deux dégradés superposés via pseudo-éléments simulés avec deux divs :
            1. Horizontal : couvre le texte à gauche, s'efface à droite
            2. Vertical bas : fondu vers la section suivante sur 12% du bas    */}
      <div
        aria-hidden="true"
        style={{
          position:      'absolute',
          inset:         0,
          zIndex:        5,
          pointerEvents: 'none',
          background: isMobile
            ? [
                // Mobile : anneau recentré derrière le texte → voile plus couvrant
                'linear-gradient(180deg, rgba(15,37,71,.74) 0%, rgba(15,37,71,.52) 45%, rgba(15,37,71,.92) 100%)',
              ].join(', ')
            : [
                // Desktop : texte lisible à gauche, anneau visible à droite
                'linear-gradient(90deg, #0F2547 0%, rgba(15,37,71,.92) 26%, rgba(15,37,71,.55) 48%, rgba(15,37,71,0) 70%)',
                // Vertical bas : fondu vers la section suivante
                'linear-gradient(to top, #0F2547 0%, rgba(15,37,71,0) 12%)',
              ].join(', '),
        }}
      />

      {/* ══ COUCHE CONTENU z-10 : overlay texte ══════════════════════════
          pointer-events:none global → seuls les éléments interactifs
          (boutons, onglets) repassent en pointer-events:auto.
          Un drag dans la zone droite (vide de texte) traverse jusqu'au canvas. */}
      <div
        className="absolute inset-0"
        style={{
          zIndex:        10,
          pointerEvents: 'none',
        }}
      >
        {/* Conteneur centré — alignement vertical + padding navbar fixe */}
        <div
          className="mx-auto px-6 h-full flex items-center"
          style={{
            maxWidth:      '1440px',
            // Padding-top : dégage la navbar fixe (~68px) + respiration
            paddingTop:    'clamp(5.5rem, 10vh, 7.5rem)',
            paddingBottom: 'clamp(2.5rem, 5vh, 4rem)',
          }}
        >

          {/* ── Colonne texte : max 560px, alignée à gauche ───────────── */}
          <div
            className="animate-mecFade flex flex-col gap-5"
            style={{
              width:    'min(560px, 100%)',
              // Sur mobile la colonne prend toute la largeur (min() le gère)
            }}
          >

            {/* Eyebrow */}
            <p
              className="font-sans font-semibold"
              style={{
                color:         'rgba(234,244,251,0.55)',
                fontSize:      '11px',
                letterSpacing: '.22em',
                textTransform: 'uppercase',
                // pointer-events:none hérité, pas d'interaction nécessaire
              }}
            >
              Progresser, ensemble
            </p>

            {/* H1 — agrandi car on a toute la hauteur d'écran disponible */}
            <h1
              className="font-display font-bold text-white"
              style={{
                fontSize:      'clamp(1.85rem, 3.6vw, 3.5rem)',
                lineHeight:    1.07,
                letterSpacing: '-.02em',
              }}
            >
              Le progrès d'un élève n'est pas un mystère.{' '}
              <span style={{ color: 'rgba(234,244,251,0.55)' }}>
                C'est un cycle.
              </span>
            </h1>

            {/* Sous-titre */}
            <p
              className="font-sans"
              style={{
                color:      'rgba(234,244,251,0.72)',
                maxWidth:   '33rem',
                lineHeight: 1.65,
                fontSize:   'clamp(.9rem, 1.1vw, 1rem)',
              }}
            >
              Parent, répétiteur, élève : trois rôles, un même fil continu.
              Séances planifiées, comptes-rendus après chaque cours, notes et
              moyennes qui s'actualisent — tout le monde voit la même
              progression, au même moment.
            </p>

            {/* ── CTA ──────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-4">

              {/* Bouton primaire orange — pointer-events:auto pour recevoir les clics */}
              <button
                onClick={() => openSignup('parent')}
                className="font-display font-semibold flex items-center gap-2 transition-colors"
                style={{
                  background:    '#FFA726',
                  color:         '#0F2547',
                  padding:       '16px 30px',
                  borderRadius:  '999px',
                  fontSize:      '0.95rem',
                  boxShadow:     '0 10px 30px -10px rgba(255,167,38,.55)',
                  border:        'none',
                  cursor:        'pointer',
                  whiteSpace:    'nowrap',
                  pointerEvents: 'auto',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f3911c'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#FFA726'; }}
              >
                Créer mon espace
                <span aria-hidden="true">→</span>
              </button>

              {/* Bouton ghost « Explorer le cycle » */}
              <button
                onClick={() => setActive(a => a < 0 ? 0 : (a + 1) % 3)}
                className="font-sans flex items-center gap-3 transition-opacity hover:opacity-80"
                style={{
                  background:    'transparent',
                  border:        'none',
                  cursor:        'pointer',
                  color:         '#EAF4FB',
                  padding:       0,
                  pointerEvents: 'auto',
                }}
                aria-label="Explorer le cycle des rôles"
              >
                {/* Rond avec glyphe ↻ */}
                <span
                  aria-hidden="true"
                  style={{
                    width:          '38px',
                    height:         '38px',
                    borderRadius:   '50%',
                    border:         '1px solid rgba(234,244,251,0.32)',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    fontSize:       '1.1rem',
                    flexShrink:     0,
                  }}
                >
                  ↻
                </span>
                <span
                  className="font-display text-sm"
                  style={{
                    color:               '#EAF4FB',
                    textDecoration:      'underline',
                    textUnderlineOffset: '3px',
                  }}
                >
                  Explorer le cycle
                </span>
              </button>
            </div>

            {/* ── Sélecteur de rôle (bloc glass) — pointer-events:auto ─── */}
            <div
              role="tablist"
              aria-label="Rôles MyEduCycle"
              style={{
                border:               '1px solid rgba(234,244,251,0.14)',
                borderRadius:         '20px',
                background:           'rgba(234,244,251,0.045)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                overflow:             'hidden',
                // Réactiver les pointer-events pour tout le bloc glass
                pointerEvents:        'auto',
              }}
            >
              {/* En-tête : 3 onglets */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                {ROLES.map((role, i) => {
                  const isActive = active === i;
                  return (
                    <button
                      key={role.label}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`role-panel-${i}`}
                      onClick={() => setActive(isActive ? -1 : i)}
                      className="font-sans font-semibold transition-colors"
                      style={{
                        padding:        '12px 8px',
                        border:         'none',
                        cursor:         'pointer',
                        background:     isActive ? 'rgba(234,244,251,0.10)' : 'transparent',
                        color:          isActive ? '#ffffff' : 'rgba(234,244,251,0.55)',
                        fontSize:       '0.78rem',
                        letterSpacing:  '.04em',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        gap:            '6px',
                        // Soulignement bas de la couleur accent si actif
                        borderBottom:   isActive
                          ? '2px solid #FFA726'
                          : '2px solid transparent',
                        transition:     'background .2s, color .2s, border-color .2s',
                      }}
                    >
                      {/* Dot accent si actif */}
                      <span
                        aria-hidden="true"
                        style={{
                          width:        '5px',
                          height:       '5px',
                          borderRadius: '50%',
                          background:   isActive ? '#FFA726' : 'rgba(234,244,251,0.30)',
                          flexShrink:   0,
                          transition:   'background .2s',
                        }}
                      />
                      {role.label}
                    </button>
                  );
                })}
              </div>

              {/* Corps du panneau : barre + titre + description */}
              <div
                id={active >= 0 ? `role-panel-${active}` : 'role-panel-default'}
                role="tabpanel"
                style={{
                  display: 'flex',
                  gap:     '14px',
                  padding: '18px 20px 20px',
                }}
              >
                {/* Barre verticale accent */}
                <div
                  aria-hidden="true"
                  style={{
                    width:        '3px',
                    borderRadius: '2px',
                    background:   '#FFA726',
                    flexShrink:   0,
                    alignSelf:    'stretch',
                    minHeight:    '40px',
                  }}
                />
                <div>
                  <p
                    className="font-display font-bold text-white"
                    style={{
                      fontSize:     'clamp(1rem, 1.3vw, 1.15rem)',
                      lineHeight:   1.25,
                      marginBottom: '8px',
                    }}
                  >
                    {panel.title}
                  </p>
                  <p
                    className="font-sans"
                    style={{
                      color:      'rgba(234,244,251,0.72)',
                      fontSize:   '0.875rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {panel.desc}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Mobile hooks (stores) ─────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="font-sans text-sm"
                style={{ color: 'rgba(234,244,251,0.72)' }}
              >
                Comptes-rendus en notification sur mobile :
              </span>
              {[
                { label: 'Google Play · bientôt' },
                { label: 'App Store · bientôt' },
              ].map(({ label }) => (
                <span
                  key={label}
                  className="font-sans font-semibold"
                  style={{
                    display:      'flex',
                    alignItems:   'center',
                    gap:          '5px',
                    padding:      '5px 12px',
                    borderRadius: '999px',
                    border:       '1px solid rgba(234,244,251,0.18)',
                    background:   'rgba(234,244,251,0.05)',
                    color:        'rgba(234,244,251,0.72)',
                    fontSize:     '0.78rem',
                    whiteSpace:   'nowrap',
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: '0.65rem' }}>▶</span>
                  {label}
                </span>
              ))}
            </div>

          </div>
          {/* fin colonne texte */}

        </div>
        {/* fin conteneur centré */}

      </div>
      {/* fin couche contenu z-10 */}

      {/* ══ COUCHE EN-TÊTE z-20 : logo + Connexion (remplace la navbar) ═════
          pointer-events:none sur le conteneur ; seuls le logo et le lien
          Connexion repassent en auto, pour ne pas bloquer le drag du canvas. */}
      <div
        className="absolute top-0 left-0 w-full"
        style={{ zIndex: 20, pointerEvents: 'none' }}
      >
        <div
          className="mx-auto px-6 flex items-center justify-between"
          style={{
            maxWidth:  '1440px',
            paddingTop: 'clamp(1.5rem, 3vh, 2.25rem)',
          }}
        >
          {/* Logo — logomark sur pastille blanche pour ressortir sur le fond nuit */}
          <a
            href="#hero"
            className="flex items-center gap-2.5"
            style={{ pointerEvents: 'auto' }}
          >
            <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-soft">
              <img
                src={logomark}
                alt="MyEduCycle"
                className="w-8 h-8 object-contain"
              />
            </span>
            <span className="text-white font-display font-bold text-xl">
              MyEduCycle
            </span>
          </a>

          {/* Connexion — pill contrasté pour la lisibilité + affordance */}
          <button
            onClick={openLogin}
            className="font-sans font-semibold text-sky border border-sky/40 hover:bg-white hover:text-primary hover:border-white rounded-full px-5 py-2 transition-colors"
            style={{ pointerEvents: 'auto' }}
          >
            Connexion
          </button>
        </div>
      </div>

    </section>
  );
}
